/**
 * La communication d'un paiement, en entier.
 *
 * ## Pourquoi ce composant existe
 *
 * Signalé au deuxième essai réel, sur un paiement de 3,00 EUR pour une facture
 * de 95,00 EUR : « il y a une communication de la maman que je n'arrive pas à
 * lire complètement... les infos qu'elle renferme peuvent être importantes ».
 *
 * Deux troncatures se cumulaient. L'écran coupait le texte pour tenir sur une
 * ligne — c'est réparable en CSS. Mais **le CODA lui-même coupe à 53
 * caractères** :
 *
 *     CDV-NDSC-2601965/Monica William 95 euros-92 euros not
 *     |------------------ exactement 53 ------------------|
 *
 * La phrase s'arrête au milieu d'un mot. Aucune mise en page ne récupère ce
 * qui n'a pas été stocké. Ce qui manque est dans le libellé complet du relevé
 * (enregistrements CODA 31 et 32), que l'import conserve désormais.
 *
 * Le texte est chargé à la demande : deux à trois cents caractères par
 * paiement n'ont pas à voyager pour une file entière.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, ScrollText } from 'lucide-react';
import { loadPaymentText, type TextePaiement } from '../../../lib/payments/actions.supabase';
import { messageErreur } from '../../../lib/payments/erreur';

/** Longueur du champ communication d'un enregistrement CODA de type 21. */
const LONGUEUR_CODA = 53;

export default function CommunicationComplete({
  paymentId,
  communicationConnue,
  texte: fourni,
}: {
  paymentId: string;
  /** Ce que la file porte déjà : évite un écran vide pendant le chargement. */
  communicationConnue: string | null;
  /**
   * Le texte déjà lu par l'écran appelant. Trois valeurs, trois sens :
   *
   * - `undefined` — personne ne s'en occupe, ce composant le lit lui-même ;
   * - `null` — l'appelant le lit, et sa lecture est en cours ;
   * - un objet — l'appelant l'a lu.
   *
   * Le libellé du relevé sert maintenant à deux panneaux de la file. Le lire
   * deux fois pour la même ligne n'apporterait rien, et le laisser lire par
   * chacun ferait diverger ce qu'ils montrent.
   */
  texte?: TextePaiement | null;
}) {
  const gereParLAppelant = fourni !== undefined;
  const [luIci, setLuIci] = useState<TextePaiement | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const texte = gereParLAppelant ? fourni : luIci;

  const charger = useCallback(async () => {
    if (gereParLAppelant) return;
    try {
      setLuIci(await loadPaymentText(paymentId));
    } catch (e) {
      setErreur(messageErreur(e, 'Lecture de la communication impossible'));
    }
  }, [paymentId, gereParLAppelant]);

  useEffect(() => { void charger(); }, [charger]);

  const communication = texte?.communicationRaw ?? communicationConnue;
  // Se mesure sur le CONTENU : le champ CODA est complété à droite par des
  // espaces, donc sa longueur brute ne dit rien.
  const coupee = (communication ?? '').trimEnd().length >= LONGUEUR_CODA;

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
        <ScrollText className="h-3.5 w-3.5" /> Communication complète
      </p>

      {erreur && <p className="mt-2 text-sm text-red-700">{erreur}</p>}

      <p className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-gray-900">
        {communication?.trim() || <em className="text-gray-400">communication vide</em>}
      </p>

      {coupee && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Ce texte occupe les {LONGUEUR_CODA} caractères du champ bancaire : la banque l'a
            probablement coupé. La suite, quand elle existe, est dans le libellé du relevé
            ci-dessous.
          </span>
        </p>
      )}

      {texte === null && !erreur && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Lecture du libellé du relevé…
        </p>
      )}

      {texte?.freeText && (
        <>
          <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500">
            Libellé complet du relevé
          </p>
          <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-gray-700">
            {texte.freeText}
          </p>
        </>
      )}

      {texte?.freeTextUnavailable && (
        <p className="mt-3 text-xs text-gray-500">
          Le libellé complet du relevé n'est pas encore conservé en base : il le sera pour les
          relevés importés après l'application de la migration <code>20260903111107</code>.
        </p>
      )}

      {texte && !texte.freeText && !texte.freeTextUnavailable && (
        <p className="mt-3 text-xs text-gray-500">
          Aucun libellé de relevé enregistré pour ce paiement — il a été importé avant que
          ce texte soit conservé.
        </p>
      )}
    </div>
  );
}
